<template>
  <section class="ui-page">
    <AppFilterToolbar>
      <template #primary>
        <AppInput
          id="trengo-ticket-search"
          v-model="search"
          design="new"
          type="search"
          aria-label="Search"
          placeholder="Ticket, contact or lead"
          @input.native="onTransactionSearch"
        />
      </template>

      <AppSelect
        v-model="filter_channel"
        design="new"
        input-id="trengo-ticket-channel"
        :options="filter_channel_options"
        label="label"
        :reduce="item => item.value"
        placeholder="Channel"
        aria-label="Channel"
        @input="onChangeFilter"
      />

      <AppSelect
        v-model="filter_label"
        design="new"
        input-id="trengo-ticket-label"
        :options="filter_label_options"
        label="label"
        :reduce="item => item.value"
        placeholder="Label"
        aria-label="Label"
        @input="onChangeFilter"
      />

      <AppSelect
        v-model="adv_source_filter"
        design="new"
        input-id="trengo-ticket-source"
        :options="advertising_source_options"
        placeholder="Advertising source"
        aria-label="Advertising source"
        @input="onInputAdvertisingSourceFilter"
      />

      <AppSelect
        v-model="adv_campaign_filter"
        design="new"
        input-id="trengo-ticket-campaign"
        :options="adv_campaign_options_filter"
        :reduce="item => item.id"
        :disabled="!adv_campaign_options_filter.length"
        placeholder="Advertising campaign"
        aria-label="Advertising campaign"
        @input="onChangeFilter"
      />

      <AppCheckbox
        v-model="filter_unallocated"
        design="new"
        name="trengo-ticket-unallocated"
        label="WA Unallocated"
        @change="onChangeFilter"
      />
      <template v-if="hasActiveFilters" #clear>
        <AppClearFiltersButton design="new" icon-only @clear="clearFilters" />
      </template>
    </AppFilterToolbar>

    <AppContentSkeleton v-if="loading" label="Loading Trengo tickets" :columns="columns" min-width="1200px" />
    <AppErrorState v-else-if="error" :message="error" @retry="fetchItems" />

    <AppCard v-else-if="items.length" design="new" variant="table" header-density="compact">
      <AppDataTable
        design="new"
        density="dense"
        :rows="items"
        :columns="columns"
        :loading="loading"
        min-width="1200px"
      >
        <template #cell-ticket="{ row }">
          <AppTableText :text="row.ticket_id ? `#${row.ticket_id}` : null">
            <RouterLink :to="{ name: 'trengo-tickets-detail', params: { id: row.id } }" class="ui-link">#{{ row.ticket_id }}</RouterLink>
          </AppTableText>
        </template>

        <template #cell-channel="{ row }">
          <AppTableText :text="row.channel && row.channel.name" />
        </template>

        <template #cell-date="{ row }">
          <AppTableText :text="dateTimeFormat(row.trengo_created_at)" />
        </template>

        <template #cell-language="{ row }">
          <AppTableText :text="row.language && row.language.name" />
        </template>

        <template #cell-country="{ row }">
          <div v-if="row.country" class="ui-cell-inline">
            <span :class="`fflag ff-sm fflag-${row.country.code || 'empty'}`" aria-hidden="true" />
            <AppTableText :text="row.country.name" />
          </div>
          <span v-else>—</span>
        </template>

        <template #cell-contact="{ row }">
          <div class="ui-actions ui-actions--tight">
            <AppIconButton :label="row.email ? 'Copy email: ' + row.email : 'Email is not available'" :disabled="!row.email" @click="onCopyContactLink(row.email)">
              <i class="pi pi-envelope" aria-hidden="true" />
            </AppIconButton>
            <AppIconButton :label="row.phone ? 'Copy phone: ' + row.phone : 'Phone is not available'" :disabled="!row.phone" @click="onCopyContactLink(row.phone)">
              <i class="pi pi-phone" aria-hidden="true" />
            </AppIconButton>
          </div>
        </template>

        <template #cell-labels="{ row }">
          <div v-if="row.labels && row.labels.length" class="ui-cell-inline">
            <AppStatusBadge
              v-for="label in row.labels"
              :key="`${label.name}-${row.id}`"
              design="new"
              compact
              :color="label.color"
              :label="label.name"
            />
          </div>
          <span v-else>—</span>
        </template>

        <template #cell-status="{ row }">
          <AppStatusBadge
            design="new"
            compact
            :tone="statusTone(row.status)"
            :label="statusLabel(row.status)"
          />
        </template>

        <template #cell-source="{ row }">
          <AppTableText :text="row.adv_source && row.adv_source.name" />
        </template>

        <template #cell-campaign="{ row }">
          <AppTableText :text="row.adv_campaign && row.adv_campaign.name" />
        </template>

        <template #cell-lead="{ row }">
          <AppTableText v-if="row.lead" :text="row.lead.name">
            <RouterLink :to="{ name: 'leads-detail-lead', params: { id: row.lead.id } }" class="ui-link">{{ row.lead.name }}</RouterLink>
          </AppTableText>
          <span v-else>—</span>
        </template>

        <template #cell-manager="{ row }">
          <AppTableUser :name="row.lead && row.lead.manager && row.lead.manager.full_name || ''" :photo="row.lead ? managerCrop(row.lead) : ''" />
        </template>
      </AppDataTable>
    </AppCard>
    <AppNoResults
      v-else
      design="new"
      title="No Trengo tickets found"
      text="Try changing the search or filters."
    />
    <AppPagination
      v-if="!loading && !error && items.length"
      design="new"
      placement="embedded"
      :page="pagination.page"
      :pages="pagination.pages"
      :count="pagination.count"
      :visible-count="items.length"
      :page-size="PAGE_SIZE"
      :loading="loading"
      label="Trengo ticket pages"
      @change="onClickPagination"
    />
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'
import { getLeadsAdvSources, getLeadsTrengoTickets } from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import { ticketStatusTone, ticketStatusLabel } from './statusPresentation'

const PAGE_SIZE = 20

export default {
  name: 'TrengoTickets',
  components: { AppContentSkeleton, AppErrorState, AppTableText, AppTableUser },
  data() {
    return {
      PAGE_SIZE,
      items: [],
      loading: false,
      error: '',
      search: '',
      filter_unallocated: false,
      filter_label: null,
      filter_label_options: [
        { value: '1387264', label: 'Lead' },
        { value: '1391494', label: 'Trash' },
        { value: '1387269', label: 'On board' }
      ],
      filter_channel: null,
      filter_channel_options: [
        { value: 'website_chat', label: 'Website chat' },
        { value: 'whatsapp', label: 'WhatsApp' }
      ],
      adv_source_filter: null,
      advertising_source_options: [],
      adv_campaign_filter: null,
      adv_campaign_options_filter: [],
      pagination: {
        pages: 1,
        page: 1,
        count: 0
      },
      requestId: 0,
      columns: [
        { key: 'ticket', label: 'Ticket', width: '8%' },
        { key: 'channel', label: 'Channel', width: '9%' },
        { key: 'date', label: 'Created at', width: '12%' },
        { key: 'language', label: 'Language', width: '7%' },
        { key: 'country', label: 'Country', width: '10%' },
        { key: 'contact', label: 'Contact', width: '8%' },
        { key: 'labels', label: 'Labels', width: '12%' },
        { key: 'status', label: 'Status', width: '8%' },
        { key: 'source', label: 'Source', width: '9%' },
        { key: 'campaign', label: 'Campaign', width: '9%' },
        { key: 'lead', label: 'Lead', width: '12%' },
        { key: 'manager', label: 'Lead manager', width: '14%' }
      ]
    }
  },
  computed: {
    hasActiveFilters() {
      return Boolean(
        this.search
        || this.filter_unallocated
        || this.filter_channel
        || this.filter_label
        || this.adv_source_filter
        || this.adv_campaign_filter
      )
    }
  },
  created() {
    this.searchTransaction = debounce(this.searchTransaction, 350)
    this.fetchItems()
    this.loadAdvertisingSources()
  },
  beforeDestroy() {
    this.searchTransaction.cancel()
    this.requestId += 1
  },
  methods: {
    async fetchItems() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const response = await getLeadsTrengoTickets({
          page: this.pagination.page,
          page_size: PAGE_SIZE,
          channel: this.search ? '' : this.filter_channel,
          labels: this.search ? '' : this.filter_label,
          adv_source: this.search ? '' : this.adv_source_filter && this.adv_source_filter.code,
          adv_campaign: this.search ? '' : this.adv_campaign_filter,
          search: this.search,
          unallocated: this.filter_unallocated || ''
        })
        if (requestId !== this.requestId) return
        this.items = response.data.results
        this.pagination.pages = response.data.pages
        this.pagination.count = response.data.count
      } catch (error) {
        if (requestId === this.requestId) this.error = this.errorMessage(error, 'Unable to load Trengo tickets.')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    async loadAdvertisingSources() {
      try {
        const response = await getLeadsAdvSources()
        this.advertising_source_options = response.data
      } catch (error) {
        this.$notify({ type: 'error', title: this.errorMessage(error, 'Unable to load advertising sources.') })
      }
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onInputAdvertisingSourceFilter(source) {
      this.adv_campaign_filter = null
      this.adv_campaign_options_filter = source ? source.campaigns : []
      this.onChangeFilter()
    },
    clearFilters() {
      this.searchTransaction.cancel()
      this.search = ''
      this.filter_unallocated = false
      this.filter_channel = null
      this.filter_label = null
      this.adv_source_filter = null
      this.adv_campaign_filter = null
      this.adv_campaign_options_filter = []
      this.pagination.page = 1
      this.fetchItems()
    },
    onTransactionSearch() {
      this.searchTransaction(this)
    },
    searchTransaction(vm) {
      vm.pagination.page = 1
      vm.fetchItems()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy, HH:mm') : '—'
    },
    statusTone: ticketStatusTone,
    statusLabel: ticketStatusLabel,
    managerCrop(lead) {
      return lead.manager && lead.manager.crop && lead.manager.crop.crop_url
    },
    onCopyContactLink(link) {
      if (!link) return
      navigator.clipboard.writeText(link)
        .then(() => this.$notify({ type: 'success', title: 'Copied to clipboard' }))
        .catch(() => this.$notify({ type: 'error', title: 'Unable to copy contact' }))
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      return (data && (data.detail || data.message)) || fallback
    }
  }
}
</script>
